import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import useFixedPortalMenu, { PORTAL_SHELL_STYLE } from "@/hooks/useFixedPortalMenu";
import { getPortalThemeProps } from "@/constants/themes";
import stripLeadingGiai from "@/utils/stripLeadingGiai";
import "./LeagueFilter.css";

function leagueKey(league) {
  return String(league?.id ?? league?.slug ?? league?.name ?? "");
}

function leagueLabel(league) {
  return stripLeadingGiai(league?.nameVN || league?.name || "");
}

function SearchGlyph() {
  return (
    <svg viewBox="0 0 20 20" aria-hidden="true">
      <circle cx="8.5" cy="8.5" r="5.25" fill="none" stroke="currentColor" strokeWidth="1.8" />
      <path d="M12.4 12.4L16.2 16.2" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function CloseGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M4.2 4.2l7.6 7.6M11.8 4.2l-7.6 7.6"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CheckGlyph() {
  return (
    <svg viewBox="0 0 16 16" aria-hidden="true">
      <path
        d="M3.2 8.2l3.1 3.2 6.5-6.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LeagueLogo({ src, label }) {
  const [broken, setBroken] = useState(false);
  const show = Boolean(src) && !broken;
  const letter = String(label || "?").trim().charAt(0).toUpperCase() || "?";
  return (
    <span className={`kbv-ltd-lg-logo ${show ? "" : "is-fallback"}`}>
      {show ? (
        <img src={src} alt="" onError={() => setBroken(true)} />
      ) : (
        <span>{letter}</span>
      )}
    </span>
  );
}

function ChipSkeleton() {
  return (
    <span className="kbv-ltd-lg-skel" aria-hidden="true">
      <span className="kbv-ltd-lg-skel-dot" />
      <span className="kbv-ltd-lg-skel-bar" />
    </span>
  );
}

export default function LeagueFilter({
  leagues = [],
  selectedIds,
  onToggle,
  onClear,
  themeStyle,
  loading = false,
  compact = false,
}) {
  const [open, setOpen] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [dropdownStyle, setDropdownStyle] = useState({});

  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const dropdownRef = useRef(null);
  const rowRef = useRef(null);
  const selectedChipRef = useRef(null);

  const isFiltering = selectedIds !== null;

  const isSelected = useCallback(
    (league) => {
      if (!isFiltering) return false;
      const id = leagueKey(league);
      return selectedIds.some((x) => String(x) === id);
    },
    [isFiltering, selectedIds],
  );

  const chips = leagues;

  const revealSelectedChip = useCallback((el) => {
    const row = rowRef.current;
    if (!row || !el) return;
    const rowRect = row.getBoundingClientRect();
    const chipRect = el.getBoundingClientRect();
    const padStart = 8;
    const padEnd = 12;
    let delta = 0;
    if (chipRect.left < rowRect.left + padStart) {
      delta = chipRect.left - rowRect.left - padStart;
    } else if (chipRect.right > rowRect.right - padEnd) {
      delta = chipRect.right - rowRect.right + padEnd;
    }
    if (Math.abs(delta) > 1) row.scrollLeft += delta;
  }, []);

  const setSelectedChipRef = useCallback(
    (node) => {
      selectedChipRef.current = node;
      if (node) revealSelectedChip(node);
    },
    [revealSelectedChip],
  );

  useLayoutEffect(() => {
    const el = selectedChipRef.current;
    if (!el) return;
    revealSelectedChip(el);
    const id = requestAnimationFrame(() => revealSelectedChip(el));
    return () => cancelAnimationFrame(id);
  }, [chips, revealSelectedChip, selectedIds]);

  const dropdownLeagues = useMemo(() => {
    const q = keyword.trim().toLowerCase();
    if (!q) return leagues;
    return leagues.filter((league) => leagueLabel(league).toLowerCase().includes(q));
  }, [keyword, leagues]);

  const close = useCallback(() => setOpen(false), []);

  const { menuStyle: compactMenuStyle, updatePosition: updateCompactPosition } = useFixedPortalMenu({
    open: Boolean(compact) && open,
    onClose: close,
    triggerRef: rootRef,
    menuRef: dropdownRef,
  });

  const updateDropdownPosition = useCallback(() => {
    if (compact) {
      updateCompactPosition();
      return;
    }
    if (!triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const gap = 8;
    const pad = 8;
    const isMobile = window.innerWidth < 768;
    setDropdownStyle({
      position: "fixed",
      top: rect.bottom + gap,
      ...(isMobile
        ? { left: pad, right: pad, width: window.innerWidth - pad * 2 }
        : { left: Math.min(rect.left, window.innerWidth - 400 - pad), width: 380 }),
      zIndex: 99999,
    });
  }, [compact, updateCompactPosition]);

  useLayoutEffect(() => {
    if (open && !compact) updateDropdownPosition();
  }, [open, compact, updateDropdownPosition]);

  useEffect(() => {
    if (!open || compact) return undefined;
    const onDoc = (e) => {
      const inTrigger = rootRef.current?.contains(e.target);
      const inDropdown = dropdownRef.current?.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onMove = () => updateDropdownPosition();
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    window.addEventListener("resize", onMove);
    window.addEventListener("scroll", onMove, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("resize", onMove);
      window.removeEventListener("scroll", onMove, true);
    };
  }, [open, compact, updateDropdownPosition]);

  const handleSelect = (league) => {
    const id = leagueKey(league);
    if (!id) return;
    onToggle?.(id);
    setOpen(false);
    setKeyword("");
  };

  const { theme: portalTheme, className: portalClassName } =
    getPortalThemeProps(rootRef.current);

  const showSkeleton = loading && !chips.length;

  if (!showSkeleton && !chips.length) return null;

  return (
    <div ref={rootRef} className={`kbv-ltd-lg${compact ? " is-compact" : ""}`}>
      <div className="kbv-ltd-lg-bar">
        <button
          ref={triggerRef}
          type="button"
          className={`kbv-ltd-lg-search ${open ? "is-open" : ""}`}
          aria-label="Tìm giải đấu"
          aria-expanded={open}
          aria-haspopup="listbox"
          onClick={() => {
            setOpen((prev) => {
              if (prev) return false;
              updateDropdownPosition();
              return true;
            });
          }}
        >
          <SearchGlyph />
          <span className="kbv-ltd-lg-search-label">Tìm giải</span>
        </button>

        <button
          type="button"
          title={isFiltering ? "Bỏ lọc" : "Tất cả"}
          aria-label={isFiltering ? "Bỏ lọc giải đấu" : "Tất cả giải đấu"}
          aria-pressed={!isFiltering}
          onClick={onClear}
          className={`kbv-ltd-lg-chip kbv-ltd-lg-all ${isFiltering ? "" : "is-active"}`}
        >
          <span className="kbv-ltd-lg-chip-name">Tất cả</span>
        </button>

        <div ref={rowRef} className="kbv-ltd-lg-row">
          {showSkeleton
            ? Array.from({ length: 7 }, (_, i) => <ChipSkeleton key={i} />)
            : chips.map((league) => {
                const selected = isSelected(league);
                const label = leagueLabel(league);
                return (
                  <button
                    key={leagueKey(league)}
                    ref={selected ? setSelectedChipRef : undefined}
                    type="button"
                    title={label}
                    aria-pressed={selected}
                    onClick={() => handleSelect(league)}
                    className={`kbv-ltd-lg-chip ${selected ? "is-active" : ""}`}
                  >
                    <LeagueLogo src={league.logo} label={label} />
                    <span className="kbv-ltd-lg-chip-name">{label}</span>
                  </button>
                );
              })}
        </div>
      </div>

      {open &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            className={portalClassName}
            data-theme={portalTheme}
            style={compact ? { ...themeStyle, ...PORTAL_SHELL_STYLE } : themeStyle}
          >
            <div
              ref={dropdownRef}
              style={compact ? { ...themeStyle, ...compactMenuStyle } : dropdownStyle}
              className="kbv-ltd-lg-drop"
              role="listbox"
              aria-label="Danh sách giải đấu"
            >
              <div className="kbv-ltd-lg-drop-head">
                <div className="kbv-ltd-lg-drop-search">
                  <SearchGlyph />
                  <input
                    type="text"
                    autoFocus
                    value={keyword}
                    onChange={(e) => setKeyword(e.target.value)}
                    placeholder="Tìm tên giải đấu..."
                  />
                </div>
                <button
                  type="button"
                  className="kbv-ltd-lg-drop-close"
                  aria-label="Đóng"
                  onClick={() => setOpen(false)}
                >
                  <CloseGlyph />
                </button>
              </div>
              <div className="kbv-ltd-lg-drop-caption">
                {keyword.trim() ? "Kết quả tìm kiếm" : "Giải đấu"}
              </div>
              <div className="kbv-ltd-lg-drop-list">
                {dropdownLeagues.length === 0 ? (
                  <div className="kbv-ltd-lg-drop-empty">Không tìm thấy giải đấu</div>
                ) : (
                  dropdownLeagues.map((league) => {
                    const selected = isSelected(league);
                    const label = leagueLabel(league);
                    return (
                      <button
                        key={leagueKey(league)}
                        type="button"
                        role="option"
                        aria-selected={selected}
                        onClick={() => handleSelect(league)}
                        className={`kbv-ltd-lg-drop-item ${selected ? "is-active" : ""}`}
                      >
                        <LeagueLogo src={league.logo} label={label} />
                        <span className="kbv-ltd-lg-drop-name">{label}</span>
                        {selected ? (
                          <span className="kbv-ltd-lg-drop-check">
                            <CheckGlyph />
                          </span>
                        ) : null}
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
